import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface ApiConsumer {
  id: number;
  name: string;
  api_key: string;
  contact_email: string | null;
  allowed_endpoints: string[] | null;
  rate_limit_per_minute: number;
  is_active: boolean;
  notes: string | null;
  last_request_at: string | null;
  created_at: string | null;
}

interface EditForm {
  name: string;
  contact_email: string;
  allowed_endpoints: string; // comma-separated in UI
  rate_limit_per_minute: number;
  is_active: boolean;
  notes: string;
}

const EMPTY_FORM: EditForm = { name: '', contact_email: '', allowed_endpoints: '', rate_limit_per_minute: 120, is_active: true, notes: '' };

const COL: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'left' };
const HDR: React.CSSProperties = { ...COL, color: '#4A5068', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #1E2330' };
const INPUT: React.CSSProperties = { width: '100%', padding: '8px 12px', backgroundColor: '#1E2330', border: '1px solid #2A3040', borderRadius: '6px', color: '#E8EAED', fontSize: '0.8rem', outline: 'none' };
const LABEL: React.CSSProperties = { display: 'block', fontSize: '0.65rem', fontWeight: 600, color: '#7B8399', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' };
const ACTION_BTN: React.CSSProperties = { width: 28, height: 28, marginLeft: 4, padding: 0, background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

export default function ConsumersPage() {
  const [rows, setRows] = useState<ApiConsumer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EditForm>(EMPTY_FORM);
  const [revealedKey, setRevealedKey] = useState<{ id: number | null; key: string } | null>(null);
  const [shownKeys, setShownKeys] = useState<Set<number>>(new Set());
  const toggleShowKey = (id: number) => {
    setShownKeys(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/consumers`).then(r => r.json()).then(setRows).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startEdit = (c: ApiConsumer) => {
    setCreating(false); setEditId(c.id);
    setForm({
      name: c.name,
      contact_email: c.contact_email || '',
      allowed_endpoints: (c.allowed_endpoints || []).join(', '),
      rate_limit_per_minute: c.rate_limit_per_minute,
      is_active: c.is_active,
      notes: c.notes || '',
    });
  };
  const startCreate = () => { setEditId(null); setCreating(true); setForm(EMPTY_FORM); setRevealedKey(null); };
  const cancel = () => { setEditId(null); setCreating(false); };

  const save = async () => {
    const payload: any = {
      name: form.name.trim(),
      contact_email: form.contact_email.trim() || null,
      allowed_endpoints: form.allowed_endpoints.trim()
        ? form.allowed_endpoints.split(',').map(s => s.trim()).filter(Boolean)
        : null,
      rate_limit_per_minute: form.rate_limit_per_minute,
      is_active: form.is_active,
      notes: form.notes.trim() || null,
    };
    if (creating) {
      const res = await fetch(`${API_BASE}/admin/consumers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const body = await res.json();
      if (res.ok && body.api_key) {
        setRevealedKey({ id: body.id, key: body.api_key });
      }
    } else if (editId) {
      await fetch(`${API_BASE}/admin/consumers/${editId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    }
    cancel(); load();
  };

  const regenerateKey = async (id: number) => {
    if (!window.confirm('API-Key neu generieren? Der alte Schlüssel wird sofort ungültig.')) return;
    const res = await fetch(`${API_BASE}/admin/consumers/${id}/regenerate-key`, { method: 'POST' });
    const body = await res.json();
    if (body.api_key) setRevealedKey({ id, key: body.api_key });
    load();
  };
  const remove = async (id: number) => {
    if (!window.confirm('Verbraucher wirklich löschen?')) return;
    await fetch(`${API_BASE}/admin/consumers/${id}`, { method: 'DELETE' });
    load();
  };
  const toggleActive = async (c: ApiConsumer) => {
    await fetch(`${API_BASE}/admin/consumers/${c.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !c.is_active }) });
    load();
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: '#E8EAED', fontSize: '1.3rem' }}>API-Verbraucher</h2>
        <button onClick={startCreate} style={{ padding: '8px 16px', background: '#4A6CF7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>+ Neuer Verbraucher</button>
      </div>

      {revealedKey && (
        <div style={{ padding: 12, marginBottom: 16, background: '#1A3A2E', border: '1px solid #2E5A4A', borderRadius: 6, color: '#A8E6B8' }}>
          <div style={{ fontSize: '0.7rem', marginBottom: 6 }}>API-Key — jetzt kopieren, wird nur einmal angezeigt!</div>
          <code style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{revealedKey.key}</code>
          <button onClick={() => { navigator.clipboard.writeText(revealedKey.key); }} style={{ marginLeft: 12, padding: '4px 10px', background: '#2A3040', border: 'none', color: '#E8EAED', borderRadius: 4, cursor: 'pointer' }}>Kopieren</button>
          <button onClick={() => setRevealedKey(null)} style={{ marginLeft: 6, padding: '4px 10px', background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 4, cursor: 'pointer' }}>OK</button>
        </div>
      )}

      {loading ? <div style={{ color: '#7B8399' }}>Laden…</div> : (
        <div style={{ background: '#15181E', border: '1px solid #1E2330', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={HDR}>Name</th>
                <th style={HDR}>API-Key</th>
                <th style={HDR}>Endpunkte</th>
                <th style={HDR}>Rate (/min)</th>
                <th style={HDR}>Aktiv</th>
                <th style={HDR}>Letzte Anfrage</th>
                <th style={HDR}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <React.Fragment key={c.id}>
                  <tr onClick={() => startEdit(c)} style={{ borderTop: '1px solid #1E2330', cursor: 'pointer', opacity: c.is_active ? 1 : 0.55 }}>
                    <td style={{ ...COL, color: '#E8EAED', fontWeight: 600 }}>{c.name}</td>
                    <td style={{ ...COL, color: '#7B8399', fontFamily: 'monospace', fontSize: '0.7rem' }} onClick={e => e.stopPropagation()}>
                      <span style={{ wordBreak: 'break-all' }}>{shownKeys.has(c.id) ? c.api_key : `${c.api_key.slice(0, 16)}…`}</span>
                      <button onClick={() => toggleShowKey(c.id)} title={shownKeys.has(c.id) ? 'Verbergen' : 'Anzeigen'} style={ACTION_BTN}>
                        <i className={`fa-solid ${shownKeys.has(c.id) ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                      <button onClick={() => { navigator.clipboard.writeText(c.api_key); }} title="Kopieren" style={ACTION_BTN}>
                        <i className="fa-solid fa-copy" />
                      </button>
                    </td>
                    <td style={{ ...COL, color: '#7B8399' }}>{c.allowed_endpoints ? c.allowed_endpoints.join(', ') : 'alle'}</td>
                    <td style={{ ...COL, color: '#7B8399' }}>{c.rate_limit_per_minute}</td>
                    <td style={COL}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.65rem', background: c.is_active ? '#1A3A2E' : '#3A1A1A', color: c.is_active ? '#A8E6B8' : '#E6A8A8' }}>{c.is_active ? 'JA' : 'NEIN'}</span></td>
                    <td style={{ ...COL, color: '#7B8399', fontSize: '0.7rem' }}>{c.last_request_at ? new Date(c.last_request_at).toLocaleString('de-DE') : '—'}</td>
                    <td style={COL} onClick={e => e.stopPropagation()}>
                      <button onClick={() => regenerateKey(c.id)} title="Key neu generieren" style={ACTION_BTN}>
                        <i className="fa-solid fa-rotate" />
                      </button>
                      <button onClick={() => toggleActive(c)} title={c.is_active ? 'Deaktivieren' : 'Aktivieren'} style={ACTION_BTN}>
                        <i className={`fa-solid ${c.is_active ? 'fa-pause' : 'fa-play'}`} />
                      </button>
                      <button onClick={() => remove(c.id)} title="Löschen" style={{ ...ACTION_BTN, color: '#E6A8A8' }}>
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </td>
                  </tr>
                  {editId === c.id && (
                    <tr><td colSpan={7} style={{ padding: 0 }}>{renderForm(form, setForm, save, cancel, false)}</td></tr>
                  )}
                </React.Fragment>
              ))}
              {creating && (
                <tr><td colSpan={7} style={{ padding: 0 }}>{renderForm(form, setForm, save, cancel, true)}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderForm(form: EditForm, setForm: (f: EditForm) => void, save: () => void, cancel: () => void, creating: boolean) {
  const set = (k: keyof EditForm, v: any) => setForm({ ...form, [k]: v });
  return (
    <div style={{ padding: 20, background: '#0F1116', borderTop: '1px solid #1E2330' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><label style={LABEL}>Name *</label><input style={INPUT} value={form.name} onChange={e => set('name', e.target.value)} /></div>
        <div><label style={LABEL}>Kontakt-E-Mail</label><input style={INPUT} value={form.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
        <div><label style={LABEL}>Erlaubte Endpunkt-Präfixe (Komma-getrennt, leer = alle)</label><input style={INPUT} value={form.allowed_endpoints} onChange={e => set('allowed_endpoints', e.target.value)} placeholder="api/internal, api/content" /></div>
        <div><label style={LABEL}>Rate Limit (Anfragen/Min)</label><input type="number" style={INPUT} value={form.rate_limit_per_minute} onChange={e => set('rate_limit_per_minute', parseInt(e.target.value) || 0)} /></div>
        <div style={{ gridColumn: '1 / span 2' }}><label style={LABEL}>Notizen</label><textarea style={{ ...INPUT, minHeight: 60 }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        <div><label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />Aktiv</label></div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button onClick={save} style={{ padding: '8px 16px', background: '#4A6CF7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>{creating ? 'Anlegen' : 'Speichern'}</button>
        <button onClick={cancel} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>Abbrechen</button>
      </div>
    </div>
  );
}
